import { useEffect, useMemo, useState } from "react";
import { isDiscountLive } from "@/hooks/useActiveDiscount";
import type { Lang } from "@/i18n/translations";
import { deriveCapacityLevel } from "@/lib/capacityLevel";
import {
  foldText,
  hasIntent,
  matchesIntent,
  matchesQuery,
  matchesTokens,
  scoreByHistory,
  topTaxonomy,
  type FilterToken,
  type IntentFilters,
} from "@/lib/feedFilter";
import type { LatLng } from "@/lib/geo";
import { isRestaurantOpen } from "@/lib/openingHours";
import { recommendVenues } from "@/lib/recommendations";
import type { SimilarityIndex } from "@/lib/similarVenues";
import { readTapHistory } from "@/lib/tapHistory";
import type { MyVisit } from "@/lib/visits";
import { useFloorSource } from "@/services/showcase";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";
import type { Cuisine, Tag } from "@/types/taxonomy";

const TICK_MS = 30_000; // re-evaluate "is open?" every 30s
// Mirrors recommendVenues' own default; named here because the optional It-7
// similarity argument sits after it in the signature.
const RECOMMENDED_MAX = 10;

interface RankedFeedInput {
  restaurants: Restaurant[];
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  signedIn: boolean;
  myVisits: MyVisit[] | null;
  similarity: SimilarityIndex | null;
  cuisines: Cuisine[];
  tags: Tag[];
  lang: Lang;
  query: string;
  activeFilters: FilterToken[];
  intent: IntentFilters;
  coords: LatLng | null;
}

/**
 * Everything the feed derives from the venue list: the open-first ordering,
 * the "Recommended for you" and "Want something quick?" picks, the quick chips,
 * and the search/filter/intent result — all re-evaluated on a 30 s clock.
 */
export function useRankedFeed({
  restaurants,
  discountsByRestaurant,
  signedIn,
  myVisits,
  similarity,
  cuisines,
  tags,
  lang,
  query,
  activeFilters,
  intent,
  coords,
}: RankedFeedInput) {
  const [now, setNow] = useState(() => new Date());
  const floorSource = useFloorSource();

  // On-device taste signal. Read ONCE per mount so the grid never reshuffles
  // under the visitor mid-session — today's taps bias the next visit.
  const tapHistory = useMemo(() => readTapHistory(), []);

  // Per-minute-ish tick so opening-hours transitions animate without a refresh.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Open venues first, live-POS venues first within each open/closed half (the
  // differentiated real-time product leads the feed — same order for guests,
  // who see those venues masked as plain "Open"), then the visitor's tap
  // history breaks the tie (venues they open / taste-alikes surface first),
  // then alphabetical. Closed venues always sink to the bottom.
  // The `motion.div layout` wrapper turns this re-sort into a slide animation.
  const orderedRestaurants = useMemo(() => {
    const nowMs = now.getTime();
    const historyScore = new Map(
      restaurants.map((r) => [r.id, scoreByHistory(r, tapHistory, nowMs)]),
    );
    return [...restaurants]
      .map((r) => ({ r, open: isRestaurantOpen(r, now) }))
      .sort((a, b) => {
        if (a.open !== b.open) return a.open ? -1 : 1;
        const posDelta =
          (b.r.pos_enabled === true ? 1 : 0) - (a.r.pos_enabled === true ? 1 : 0);
        if (posDelta !== 0) return posDelta;
        const scoreDelta = (historyScore.get(b.r.id) ?? 0) - (historyScore.get(a.r.id) ?? 0);
        if (Math.abs(scoreDelta) > 1e-9) return scoreDelta;
        return a.r.name.localeCompare(b.r.name);
      });
  }, [restaurants, now, tapHistory]);

  // "Recommended for you" picks — visit/rating taste profile + tap-history
  // nudge, falling back to paying venues when the account has no data yet
  // (see lib/recommendations.ts). Guests never compute it (strip is hidden).
  const recommendations = useMemo(
    () =>
      signedIn && myVisits !== null
        ? recommendVenues(
            orderedRestaurants,
            myVisits,
            tapHistory,
            now.getTime(),
            RECOMMENDED_MAX,
            similarity ?? undefined,
          )
        : { items: [], personalized: false },
    [signedIn, myVisits, orderedRestaurants, tapHistory, now, similarity],
  );

  // "Want something quick?" — §28-flagged fast venues, feed order, capped like
  // NEARBY_MAX. Strict === true so pre-migration rows (undefined) stay out.
  const fastVenues = useMemo(
    () => orderedRestaurants.filter(({ r }) => r.is_fast_venue === true).slice(0, 12),
    [orderedRestaurants],
  );

  // Top-10 most-used cuisines/tags across this city's venues — the quick chips.
  const topChips = useMemo(
    () => topTaxonomy(restaurants, cuisines, tags, lang, 10),
    [restaurants, cuisines, tags, lang],
  );

  // Venues with an offer inside its time window right now — the "with a deal"
  // intent. Same `isDiscountLive` the card badge and Offers tab use, so all
  // three agree on what "live" means. Re-evaluated on the 30 s tick.
  const liveDiscountIds = useMemo(() => {
    const ids = new Set<RestaurantId>();
    for (const [id, list] of Object.entries(discountsByRestaurant)) {
      if (list.some((d) => isDiscountLive(d, now))) ids.add(id);
    }
    return ids;
  }, [discountsByRestaurant, now]);

  // Apply search + active filters + concierge intents (AND) to the open-first
  // ordering.
  const visibleRestaurants = useMemo(() => {
    const folded = foldText(query);
    const intentOn = hasIntent(intent);
    // Opportunistic read of the shared floor bus: whatever the cards have
    // already hydrated, the capacity intent filters on, so a "not crowded"
    // result never contradicts the level printed on its own card. Read-only —
    // subscribing here would boot a WebSocket (and a full Supabase hydration)
    // for every visitor, guests included.
    const floor = intentOn ? floorSource.getState() : null;
    return orderedRestaurants.filter(({ r, open }) => {
      if (!matchesQuery(r, folded, cuisines, tags, lang)) return false;
      if (!matchesTokens(r, activeFilters)) return false;
      if (!intentOn) return true;
      // Live capacity exists only for POS venues, and only for account holders.
      const level =
        signedIn && r.pos_enabled === true
          ? floor?.[r.id]?.level ??
            deriveCapacityLevel(r.currently_occupied_tables, r.capacity_seats)
          : null;
      return matchesIntent(r, intent, {
        open,
        signedIn,
        coords,
        hasLiveDiscount: liveDiscountIds.has(r.id),
        level,
      });
    });
  }, [
    orderedRestaurants,
    query,
    activeFilters,
    intent,
    cuisines,
    tags,
    lang,
    signedIn,
    coords,
    liveDiscountIds,
    floorSource,
  ]);

  return { now, orderedRestaurants, recommendations, fastVenues, topChips, visibleRestaurants };
}
